import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import Table from "./../Theme/Table.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../Spinner";
import TripsActionButton from "./TripsActionButton.js";
import { Checkbox, Modal } from "antd";
import { Button } from "react-bootstrap";
import { handleExportClick } from "../exportFile.js";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { RoleContext } from "../RoleContext";

const CompanyTripsList = () => {
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getTrips = async () => {
    try {
      const res = await axiosInstance.get("/companyTrips");
      const tripsArr = Array.isArray(res.data) ? res.data : [];
      const normalized = tripsArr.map((t) => {
        const passengerName = t.passenger
          ? `${t.passenger.firstName || ""} ${t.passenger.lastName || ""}`.trim()
          : "";
        const driverName = t.driver2
          ? `${t.driver2.firstName || ""} ${t.driver2.lastName || ""}`.trim()
          : "";
        return {
          ...t,
          passenger: t.passenger ? { ...t.passenger, name: passengerName } : t.passenger,
          driver2: t.driver2 ? { ...t.driver2, name: driverName } : t.driver2,
        };
      });
      setTrips(normalized);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error("Something went wrong ...");
    }
  };

  useEffect(() => {
    getTrips();
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Passenger Information",
        columns: [
          { Header: "ID", accessor: "id" },
          { Header: "First Name", accessor: "passenger.firstName" },
          { Header: "Last Name", accessor: "passenger.lastName" },
          { Header: "Phone Number", accessor: "passenger.phoneNumber" },
        ],
      },
      {
        Header: "Driver Information",
        columns: [
          { Header: "First Name", accessor: "driver2.firstName" },
          { Header: "Last Name", accessor: "driver2.lastName" },
          { Header: "Phone Number", accessor: "driver2.phoneNumber" },
          {
            Header: "Status",
            accessor: "status",
            disableFilters: true,
            Cell: (row) => {
              const status = row.cell.row.original.status || "Active";
              return (
                <Chip
                  label={status}
                  color={status === "Active" ? "success" : "warning"}
                  variant="filled"
                  sx={{
                    fontWeight: "bold",
                    borderRadius: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                />
              );
            },
          },
          {
            Header: "Action",
            accessor: "action",
            disableFilters: true,
            Cell: (row) => (
              <div>
                {role.filter((e) => e.perm_name === "list_all_trips").length > 0 && (
                  <Tooltip title="View trip">
                    <span>
                      <VisibilityIcon
                        color="success"
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/tripDetail/${row.cell.row.original.id}`)}
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ],
      },
    ],
    []
  );

  const columns_expo = React.useMemo(
    () =>
      [
        { columns: [{ Header: "Id", accessor: "id" }] },
        {
          Header: "Passenger",
          columns: [
            { Header: "Passenger Name", accessor: "passenger.name" },
            { Header: "Passenger Phone", accessor: "passenger.phoneNumber" },
          ],
        },
        {
          Header: "Driver",
          columns: [
            { Header: "Driver Name", accessor: "driver2.name" },
            { Header: "Driver Phone", accessor: "driver2.phoneNumber" },
          ],
        },
        { Header: "Other", columns: [{ Header: "Date", accessor: "createdAt" }] },
      ].flatMap((group) => group.columns),
    []
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(columns_expo.map((c) => c.Header));

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  function handleColumnToggle(columnHeader) {
    setSelectedColumns((prev) => {
      if (prev.includes(columnHeader)) return prev.filter((c) => c !== columnHeader);
      return [...prev, columnHeader];
    });
  }

  const handleOkModal = () => {
    setIsModalVisible(false);
    handleExportClick(trips, columns_expo, "Company Trips", "company_trips.pdf", selectedColumns);
  };

  const printableRef = useRef(null);

  return (
    <div className="content-wrappers" style={{ marginTop: "-40px" }}>
      <div className="content-wrappers">
        <br />
        <br />
        <ToastContainer />

        <section className="content" ref={printableRef}>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <TripsActionButton title="Company Trips" showModal={showModal} />

                  <Modal
                    style={{ marginTop: "60px" }}
                    title="Select Columns to Export"
                    visible={isModalVisible}
                    onOk={handleOkModal}
                    onCancel={handleCancel}
                    footer={[
                      <Button key="cancel" onClick={handleCancel}>
                        Cancel
                      </Button>,
                      <Button key="ok" type="primary" onClick={handleOkModal} style={{ marginLeft: "10px" }}>
                        OK
                      </Button>,
                    ]}
                  >
                    <div style={{ marginBottom: "20px" }}>
                      {columns_expo.map((column) => (
                        <div key={column.Header} style={{ marginBottom: "10px" }}>
                          <Checkbox
                            checked={selectedColumns.includes(column.Header)}
                            onChange={() => handleColumnToggle(column.Header)}
                            style={{ marginRight: "10px", fontSize: "16px" }}
                          >
                            {column.Header}
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </Modal>

                  <div className="card-body">
                    {loading ? (
                      <div>
                        <Spinner />
                      </div>
                    ) : (
                      <Table columns={columns} data={trips} edit={"trip"} detail={"viewTrip"} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CompanyTripsList;
